import { useEffect, useState } from 'react';
import { getS3PresignedUrl } from '@/utils/s3';
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import {
  Eye as EyeIcon,
  File as FileIcon,
  Image as ImageIcon,
  Paperclip as PaperclipIcon,
  FilePdf as PdfIcon,
  PlusCircle as PlusCircleIcon,
  X as XIcon,
} from '@phosphor-icons/react';

import type { File } from '@/types/v2/file';
import type { Request } from '@/types/v2/request';
import { useDialog } from '@/hooks/v2/use-dialog';
import { PropertyList } from '@/components/core/v2/property-list';

import AddAttachmentsDialog from './add-attachment-dialog';

interface RequestAttachmentCardProps {
  request: Request;
  isLoading: boolean;
  error: Error | null;
}

// Create a styled component for a softer animation and different color
const StyledCircularProgress = styled(CircularProgress)({
  color: '#3f51b5', // Change color to a softer blue
  animationDuration: '550ms', // Adjust animation duration for a softer effect
  height: '30px',
  width: '30px',
});

export function RequestAttachmentCard({ request, isLoading, error }: RequestAttachmentCardProps): React.JSX.Element {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string>('');
  const [fileLoadError, setFileLoadError] = useState<boolean>(false);
  const [isFileLoading, setIsFileLoading] = useState<boolean>(false);
  const [csvData, setCsvData] = useState<string[][] | null>(null);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const dialog = useDialog();

  useEffect(() => {
    if (selectedFile) {
      setIsFileLoading(true);
      getS3PresignedUrl(selectedFile)
        .then((url) => {
          setFileUrl(url);
          setFileLoadError(false);
        })
        .catch(() => {
          setFileLoadError(true);
        })
        .finally(() => {
          setIsFileLoading(false);
        });
    }
  }, [selectedFile]);

  const handleFileClick = (file: File) => {
    setSelectedFile(file);
  };

  const handleCloseDialog = () => {
    setSelectedFile(null);
    setFileUrl('');
    setFileLoadError(false);
  };

  const handleImageError = () => {
    setFileLoadError(true);
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('image')) {
      return <ImageIcon />;
    } else if (fileType.includes('pdf')) {
      return <PdfIcon />;
    } 
      return <FileIcon />;
    
  };

  const renderFilePreview = () => {
    if (fileLoadError && selectedFile) {
      return (
        <div>
          <Typography color="error" variant="h6">
            Error loading file: {selectedFile.name}
          </Typography>
          <Typography color="textSecondary" variant="body2">
            File type: {selectedFile.type}
          </Typography>
        </div>
      );
    } else if (isFileLoading) {
      return (
        <Box alignItems="center" display="flex" height="50vh" justifyContent="center">
          <StyledCircularProgress size={60} thickness={4} />
        </Box>
      );
    } else if (selectedFile) {
      if (selectedFile.type.includes('pdf')) {
        return (
          <iframe src={fileUrl} style={{ width: '100%', height: '80vh', border: 'none' }} title={selectedFile.name} />
        );
      } else if (selectedFile.type.includes('csv')) {
        return (
          <Paper sx={{ padding: 2, textAlign: 'center' }}>
            <Typography gutterBottom variant="h6">
              {selectedFile.name}
            </Typography>
            <Typography color="textSecondary" gutterBottom variant="body2">
              This CSV file cannot be previewed directly. Please download it to view the contents.
            </Typography>
            <Button
              color="primary"
              download={selectedFile.name}
              href={fileUrl}
              sx={{ marginTop: 2 }}
              variant="contained"
            >
              Download CSV
            </Button>
          </Paper>
        );
      } else if (selectedFile.type.includes('image')) {
        return (
          <img
            alt={selectedFile.name}
            loading="lazy"
            onError={handleImageError}
            src={fileUrl}
            style={{ width: '100%', height: 'auto' }}
          />
        );
      } 
        return (
          <Box p={2} textAlign="center">
            <div>This file type cannot be previewed. Please download it to view.</div>
            <Button
              color="primary"
              download={selectedFile.name}
              href={fileUrl}
              style={{ marginTop: '10px' }}
              variant="contained"
            >
              Download
            </Button>
          </Box>
        );
      
    }
    return null;
  };

  return (
    <>
      <Card>
        <CardHeader
          action={
            <IconButton component="span" onClick={dialog.handleOpen}>
                <PlusCircleIcon />
              </IconButton>
          }
          avatar={
            <Avatar>
              <PaperclipIcon fontSize="var(--Icon-fontSize)" />
            </Avatar>
          }
          title="Attachments"
        />
        <PropertyList
          divider={<Divider />}
          orientation="vertical"
          sx={{
            '--PropertyItem-padding': '12px 24px',
            maxHeight: '400px',
            overflowY: 'auto',
          }}
        >
          {isLoading ? (
            <Box alignItems="center" display="flex" height="100px" justifyContent="center">
              <StyledCircularProgress size={40} thickness={4} />
            </Box>
          ) : request?.files && request.files.length > 0 ? (
            <List>
              {request.files.map((file, index) => (
                <ListItem
                  alignItems="flex-start"
                  button
                  key={index}
                  onClick={() => { handleFileClick(file as unknown as File); }}
                >
                  <ListItemAvatar>
                    <Avatar>{getFileIcon(file.type)}</Avatar>
                  </ListItemAvatar>
                  <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                    <ListItemText
                      primary={file.name}
                      primaryTypographyProps={{ noWrap: false }}
                      secondary={file.type}
                      secondaryTypographyProps={{ noWrap: false }}
                      sx={{
                        '& .MuiListItemText-primary': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </Box>
                  <IconButton edge="end" onClick={() => { handleFileClick(file as unknown as File); }}>
                    <EyeIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box p={2}>No attachments</Box>
          )}
        </PropertyList>

        <Dialog fullWidth maxWidth="lg" onClose={handleCloseDialog} open={Boolean(selectedFile)}>
          <Box alignItems="center" display="flex" justifyContent="flex-end" p={1}>
            <IconButton onClick={handleCloseDialog}>
              <XIcon />
            </IconButton>
          </Box>
          <DialogContent>{renderFilePreview()}</DialogContent>
        </Dialog>
      </Card>
      <AddAttachmentsDialog onClose={dialog.handleClose} open={dialog.open} />
    </>
  );
}
